import { Command } from 'commander';
import { promises as fs } from 'fs';
import { join } from 'path';
import { ValidationError } from '../../errors/index.js';
import { ProjectConfigService, type RulesConfig } from '../../services/project-config.service.js';
import { IDE_FORMATS } from '../../config/ide-formats.js';

interface PresetMetadata {
    slug: string;
    name: string;
    description: string;
    version: string;
    tags: string[];
    stack: Record<string, string | null>;
}

export function createRulesInfoCommand(): Command {
    return new Command('info')
        .description('Show current preset information')
        .option('--json', 'Output as JSON')
        .action(async (options) => {
            // Load project config
            const projectConfigService = new ProjectConfigService();
            const rulesConfig = await projectConfigService.loadRules();

            if (!rulesConfig) {
                throw new ValidationError(
                    'No .jai1/project.json found. Run "jai1 rules apply" first.'
                );
            }

            if (options.json) {
                console.log(JSON.stringify(rulesConfig, null, 2));
                return;
            }

            console.log('📋 Current Preset Information\n');

            // Try to read detailed preset info from .jai1/rule-preset/preset.json
            const rulePresetDir = join(process.cwd(), '.jai1', 'rule-preset');
            const presetJsonPath = join(rulePresetDir, 'preset.json');
            let presetMetadata: PresetMetadata | null = null;
            let presetFiles: string[] = [];

            try {
                const presetContent = await fs.readFile(presetJsonPath, 'utf-8');
                presetMetadata = JSON.parse(presetContent) as PresetMetadata;

                // Count files in preset directory
                const files = await fs.readdir(rulePresetDir);
                presetFiles = files.filter(f => f.endsWith('.mdc'));
            } catch {
                // Preset directory doesn't exist or is corrupted
            }

            // Show preset info
            if (presetMetadata) {
                console.log(`Preset:       ${presetMetadata.name} (${presetMetadata.slug})`);
                console.log(`Version:      ${presetMetadata.version}`);
                console.log(`Description:  ${presetMetadata.description}`);
                if (presetMetadata.tags && presetMetadata.tags.length > 0) {
                    console.log(`Tags:         ${presetMetadata.tags.join(', ')}`);
                }
            } else {
                console.log(`Preset:       ${rulesConfig.preset}`);
                console.log(`Version:      ${rulesConfig.version}`);
            }

            console.log(`Applied at:   ${new Date(rulesConfig.appliedAt).toLocaleString()}`);

            // Show source of truth
            console.log(`\nSource of Truth:`);
            if (presetFiles.length > 0) {
                console.log(`  📁 .jai1/rule-preset/ (${presetFiles.length} files)`);
                for (const file of presetFiles.slice(0, 5)) {
                    console.log(`     • ${file}`);
                }
                if (presetFiles.length > 5) {
                    console.log(`     ... and ${presetFiles.length - 5} more`);
                }
            } else {
                console.log(`  ⚠️  .jai1/rule-preset/ not found`);
                console.log(`     Run "jai1 rules apply" to create it`);
            }

            // Show configured IDEs
            if (rulesConfig.ides && rulesConfig.ides.length > 0) {
                console.log(`\nConfigured IDEs (${rulesConfig.ides.length}):`);
                for (const ideId of rulesConfig.ides) {
                    const format = IDE_FORMATS[ideId];
                    if (format) {
                        const exists = await checkIdeFilesExist(ideId, format);
                        const status = exists ? '✓' : '✗';
                        console.log(`  ${status} ${format.name} (${format.description})`);
                    }
                }
            }

            // Show backups
            if (rulesConfig.backups && rulesConfig.backups.length > 0) {
                console.log(`\nAvailable Backups (${rulesConfig.backups.length}):`);
                for (const backup of rulesConfig.backups.slice(0, 3)) {
                    const timestamp = new Date(backup.timestamp).toLocaleString();
                    console.log(`  • ${timestamp} - IDEs: ${backup.ides.join(', ')}`);
                }
                if (rulesConfig.backups.length > 3) {
                    console.log(`  ... and ${rulesConfig.backups.length - 3} more`);
                }
            }

            console.log('\nℹ️  Commands:');
            console.log('  • "jai1 rules sync" - Regenerate IDE outputs after editing .jai1/rule-preset/');
            console.log('  • "jai1 rules restore" - Restore from backup');
            console.log('  • "jai1 rules apply" - Apply a different preset (replaces current)');
        });
}

async function checkPathExists(path: string): Promise<boolean> {
    try {
        await fs.access(join(process.cwd(), path));
        return true;
    } catch {
        return false;
    }
}

async function checkIdeFilesExist(ideId: string, format: any): Promise<boolean> {
    try {
        if (ideId === 'agentsmd') {
            return await checkPathExists('AGENTS.md');
        } else if (ideId === 'gemini') {
            return await checkPathExists('GEMINI.md');
        } else {
            return await checkPathExists(format.rulesPath);
        }
    } catch {
        return false;
    }
}
